import { GetServerSidePropsContext, GetServerSidePropsResult } from "next";
import { getAgencyIdFromRequest, getLanguageFromRequest } from "../utils";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import sdk from "../sdk";
import i18next from "i18next";
import slugify from "slugify";
import { useAgency } from "../hooks/use-agency";
import {
  Box,
  Button,
  CircularProgress,
  Container,
  Drawer,
  FormControl,
  Grid,
  Hidden,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Typography,
} from "@mui/material";
import {
  PropertyProfile,
  WebsiteFilterPropertiesDto,
} from "@serviestate/shared";
import { useTranslation } from "next-i18next";
import { BsFilter } from "react-icons/bs";
import { PropertyCard } from "../components/properties/property-card";
import { useCallback, useEffect, useState } from "react";
import { AiOutlineClose } from "react-icons/ai";
import { customScrollbar } from "../components/agency-theme";
import { NextSeo } from "next-seo";
import FilterForm from "../components/filter-form";
import { useRouter } from "next/router";
import { ParsedUrlQueryInput } from "querystring";
import { track } from "../tracking";

export async function getServerSideProps({
  req,
  query,
  resolvedUrl,
}: GetServerSidePropsContext<any>): Promise<
  GetServerSidePropsResult<TransactionalProps>
> {
  const locale = getLanguageFromRequest(req);
  console.log("locale", locale);

  const agencyId = getAgencyIdFromRequest(req);

  const filters: WebsiteFilterPropertiesDto = {
    agency_id: agencyId,
    skip: 0,
    take: 9,
    geo_zone_id: (query["geo-zone-id"] as string) ?? null,
    property_type_id: query["type-id"]
      ? parseInt(query["type-id"] as string, 10)
      : null,
    transaction_type_id: query.transactionTypeId
      ? parseInt(query.transactionTypeId as string, 10)
      : null,
    order_by: (query["order-by"] as string) ?? "price",
    min_price: query["min-price"]
      ? parseInt(query["min-price"] as string, 10)
      : null,
    max_price: query["max-price"]
      ? parseInt(query["max-price"] as string, 10)
      : null,
    available: query["available"] === "true" ? true : null,
    reference: (query["ref"] as string) ?? null,
    property_subtype_id: query["subtype"]
      ? parseInt(query["subtype"] as string, 10)
      : null,
    min_bedrooms: query["min-bedrooms"]
      ? parseInt(query["min-bedrooms"] as string, 10)
      : null,
    min_bathrooms: query["min-bathrooms"]
      ? parseInt(query["min-bathrooms"] as string, 10)
      : null,
    min_surface: query["min-surface"]
      ? parseInt(query["min-surface"] as string, 10)
      : null,
    extras: query["extras"] ? (query["extras"] as string).split(",") : null,
    conservation: query["conservation"]
      ? (query["conservation"] as string).split(",")
      : null,
  };

  console.time("countProperties");

  const [total, properties, locales] = await Promise.all([
    sdk.website.countProperties(filters),
    sdk.website.filterProperties(filters),
    serverSideTranslations(locale, ["common"]),
  ]);

  track(req, resolvedUrl, "transactional");

  const result: GetServerSidePropsResult<TransactionalProps> = {
    props: {
      ...locales,
      filters,
      properties,
      total,
    },
  };

  console.timeEnd("countProperties");

  return result;
}

export type TransactionalProps = {
  filters: WebsiteFilterPropertiesDto;
  properties: PropertyProfile[];
  total: number;
};

export default function Transactional({
  properties,
  filters,
  total,
}: TransactionalProps) {
  const [page, setPage] = useState(1);
  const [noMoreData, setNoMoreData] = useState(false);
  const [loading, setLoading] = useState(false);
  const [stateProperties, setStateProperties] =
    useState<PropertyProfile[]>(properties);

  const navigate = useRouter();
  const [showFilter, setShowFilters] = useState(false);
  const { t, i18n } = useTranslation();
  const agency = useAgency();
  const [tempFilters, setTempFilters] = useState(filters);

  const transactionType = agency.info.transactions.find(
    (x) => x.id === filters.transaction_type_id
  );

  const transactionName = t(
    `transaction.type_${transactionType?.id}`,
    transactionType?.name ?? ""
  );

  const handleSearch = useCallback(
    async (form: WebsiteFilterPropertiesDto) => {
      setTempFilters(form);

      const transactionType = agency.info.transactions.find(
        (x) => x.id === form.transaction_type_id
      );

      const transactionName = t(
        `transaction.type_${transactionType?.id}`,
        transactionType?.name ?? ""
      );

      const query: ParsedUrlQueryInput = {
        "geo-zone-id": form.geo_zone_id,
        "type-id": form.property_type_id,
        "order-by": form.order_by,
        ref: form.reference,
        subtype: form.property_subtype_id,
        "min-price": form.min_price,
        "max-price": form.max_price,
        "min-bedrooms": form.min_bedrooms,
        "min-bathrooms": form.min_bathrooms,
        "min-surface": form.min_surface,
        available: form.available,
        extras: form.extras?.join(","),
        conservation: form.conservation?.join(","),
      };

      for (const key in query)
        if (
          query[key] === null ||
          query[key] === undefined ||
          query[key] === ""
        )
          delete query[key];

      if (form)
        await navigate.push(
          {
            pathname: `/${i18n.language}/${slugify(transactionName, {
              lower: true,
            })}`,
            query,
          },
          undefined
        );
    },
    [navigate, i18n, t, agency]
  );

  const handleToggleAvailble = useCallback(() => {
    handleSearch({
      ...filters,
      available: filters.available === true ? null : true,
    });
  }, [filters, handleSearch]);

  const handleChangeOrder = useCallback(
    (e: SelectChangeEvent<string>) => {
      handleSearch({ ...filters, order_by: e.target.value });
    },
    [filters, handleSearch]
  );

  useEffect(() => {
    setTempFilters((x) =>
      x.transaction_type_id !== filters.transaction_type_id ? filters : x
    );
    setStateProperties(properties);
    setPage(1);
    setNoMoreData(false);
  }, [filters, properties]);

  useEffect(() => {
    let internalNoMoreData = false;
    let internalLoading = false;
    let internalPage = 1;

    setNoMoreData(false);
    setLoading(false);
    setPage(1);

    const handleLoadMore = async () => {
      setLoading(true);
      internalLoading = true;

      const response = await fetch(`/api/properties/search`, {
        method: "POST",
        body: JSON.stringify({
          ...filters,
          skip: internalPage * 9,
          take: 9,
        }),
      });
      const propertiesToAppend = await response.json();

      if (propertiesToAppend.length < 9) {
        internalNoMoreData = true;
        setNoMoreData(true);
      }

      setStateProperties((c) => {
        const o = [...c, ...propertiesToAppend];
        return o;
      });

      internalPage += 1;
      internalLoading = false;

      setPage(internalPage);
      setLoading(false);
    };

    const handleScroll = () => {
      const element = document.getElementById("load-more-target");
      let fetching = false;
      if (
        !internalNoMoreData &&
        !internalLoading &&
        !fetching &&
        element &&
        window.innerHeight + document.documentElement.scrollTop >=
          element.offsetTop - 200
      ) {
        fetching = true;
        handleLoadMore().finally(() => {
          fetching = false;
        });
      }
    };

    window.addEventListener("scroll", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [filters]);

  return (
    <>
      <NextSeo title={`Inmuebles - ${agency?.name}`} />
      <Container sx={{ mt: 4 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 2,
          }}
        >
          <Typography variant="h1" fontSize={{ xs: 28, md: 30 }}>
            {t("transactional.list_of_properties")}
          </Typography>
          <Box>
            <Button
              variant="contained"
              sx={{ display: "flex", gap: 1 }}
              color="secondary"
              onClick={() => setShowFilters(true)}
            >
              <BsFilter size={22} />
              <Hidden mdDown>{t("transactional.see_filters")}</Hidden>
            </Button>
          </Box>
        </Box>
        <Paper
          sx={{
            py: 2,
            px: 3,
            mt: 3,
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            gap: 1,
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Typography fontWeight={600}>
            {transactionName}:{" "}
            {t("transactional.we_have_properties", {
              count: total,
            })}
          </Typography>
          <Box
            sx={{
              display: "flex",
              gap: { xs: 1, md: 2 },
              flexDirection: { xs: "column", md: "row" },
            }}
          >
            <Button
              variant="outlined"
              color="secondary"
              onClick={handleToggleAvailble}
            >
              {filters.available
                ? t("transactional.see_all_properties")
                : t("transactional.show_available_only")}
            </Button>
            <Box />
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>{t("transactional.sort_by")}</InputLabel>
              <Select
                label={t("transactional.sort_by")}
                value={filters.order_by}
                onChange={handleChangeOrder}
              >
                <MenuItem value="-published_at">
                  {t("transactional.newest")}
                </MenuItem>
                <MenuItem value="published_at">
                  {t("transactional.oldest")}
                </MenuItem>
                <MenuItem value="price">{t("transactional.cheapest")}</MenuItem>
                <MenuItem value="-price">
                  {t("transactional.more_expensive")}
                </MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Paper>
        <Grid container sx={{ mt: 1 }} spacing={2}>
          {stateProperties.map((x) => (
            <Grid key={`property_${x.reference}`} item xs={12} sm={6} md="auto">
              <PropertyCard
                property={x}
                sx={{ height: "100%" }}
                transactionTypeId={filters.transaction_type_id}
              />
            </Grid>
          ))}
        </Grid>
        <Box
          id="load-more-target"
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            visibility: loading ? "visible" : "hidden",
          }}
        >
          <CircularProgress />
        </Box>
      </Container>

      <Drawer
        anchor="left"
        open={showFilter}
        onClose={() => setShowFilters(false)}
      >
        <Box
          sx={{
            maxWidth: 300,
            width: "100vw",
            height: "100vh",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              py: 1,
              px: 2,
              borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
            }}
          >
            <Typography fontSize={18}>
              {t("transactional.advanced_search")}
            </Typography>
            <IconButton onClick={() => setShowFilters(false)}>
              <AiOutlineClose size={18} />
            </IconButton>
          </Box>
          <Box
            sx={{
              overflowY: "auto",
              overflowX: "hidden",
              p: 2,
              ...customScrollbar,
            }}
          >
            <FilterForm filters={tempFilters} onSearch={handleSearch} />
          </Box>
        </Box>
      </Drawer>
    </>
  );
}
